import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Store,
  ShoppingCart,
  Bot,
  Link as LinkIcon,
  BarChart3,
  CheckCircle2,
  Handshake,
  Shield,
  LineChart,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

export default function Home() {
  const { language } = useLanguage();

  const pains = [
    {
      icon: Store,
      title: language === 'pt' ? 'O medo de começar errado' : 'The fear of starting wrong',
      description:
        language === 'pt'
          ? 'Estruturamos sua loja profissionalmente para que você foque em vender e não em configurar.'
          : 'We set up your store professionally so you can focus on selling and not configuring.',
    },
    {
      icon: ShoppingCart,
      title: language === 'pt' ? 'Vendas perdidas no carrinho' : 'Sales lost in the cart',
      description:
        language === 'pt'
          ? 'Implementamos automações de recuperação que resgatam vendas e clientes sem que você mova um dedo.'
          : 'We implement recovery automations that rescue sales and customers without you lifting a finger.',
    },
    {
      icon: Bot,
      title: language === 'pt' ? 'Atendimento que trava o crescimento' : 'Support that bottlenecks growth',
      description:
        language === 'pt'
          ? 'Liberamos sua equipe com inteligência artificial que resolve dúvidas em segundos e atende a qualquer hora.'
          : 'We free up your team with AI that resolves queries in seconds and provides support at any time.',
    },
    {
      icon: LinkIcon,
      title: language === 'pt' ? 'Sua operação refém do trabalho manual' : 'Your operation held hostage by manual work',
      description:
        language === 'pt'
          ? 'Eliminamos o trabalho braçal conectando suas ferramentas. Menos erro humano e muito mais velocidade.'
          : 'We eliminate grunt work by connecting your tools. Less human error and much more speed.',
    },
    {
      icon: BarChart3,
      title: language === 'pt' ? 'Gestão baseada em achismos' : 'Management based on guesswork',
      description:
        language === 'pt'
          ? 'Transformamos números complexos em decisões rápidas com dashboards que mostram o lucro real e os gargalos.'
          : 'We turn complex numbers into quick decisions with dashboards showing real profit and bottlenecks.',
    },
  ];

  const pillars = [
    {
      icon: Store,
      number: '01',
      title: language === 'pt' ? 'Estrutura sólida desde o início' : 'Solid structure from the start',
      description:
        language === 'pt'
          ? 'Criamos sua loja com a base necessária para suportar o crescimento sem dores de cabeça futuras.'
          : 'We build your store with the necessary foundation to support growth without future headaches.',
    },
    {
      icon: ShoppingCart,
      number: '02',
      title: language === 'pt' ? 'Recuperação ativa de receita' : 'Active revenue recovery',
      description:
        language === 'pt'
          ? 'Automatizamos a reconquista de carrinhos abandonados. Onde há perda de conversão nós criamos lucro.'
          : 'We automate abandoned cart recovery. Where there is a loss of conversion we create profit.',
    },
    {
      icon: Bot,
      number: '03',
      title: language === 'pt' ? 'Atendimento inteligente 24 horas' : '24-hour intelligent support',
      description:
        language === 'pt'
          ? 'Integramos a IA ideal para o seu momento e garantimos respostas rápidas e um suporte que realmente escala.'
          : 'We integrate the ideal AI for your current stage and ensure quick responses and support that truly scales.',
    },
    {
      icon: LinkIcon,
      number: '04',
      title: language === 'pt' ? 'Operação no piloto automático' : 'Operation on autopilot',
      description:
        language === 'pt'
          ? 'Conectamos seu e-commerce com CRM e ERP para que as informações fluam sem intervenção manual.'
          : 'We connect your e-commerce with CRM and ERP so information flows without manual intervention.',
    },
    {
      icon: BarChart3,
      number: '05',
      title: language === 'pt' ? 'Decisões focadas em lucro' : 'Profit-focused decisions',
      description:
        language === 'pt'
          ? 'Entregamos relatórios em tempo real para você acompanhar as vendas e a saúde financeira do negócio.'
          : 'We deliver real-time reports so you can track sales and the financial health of the business.',
    },
  ];

  const reasons = [
    {
      icon: Handshake,
      title: language === 'pt' ? 'Parceiro de tecnologia e negócios' : 'Tech and business partner',
      description:
        language === 'pt'
          ? 'Entramos para estruturar a base tecnológica de quem quer crescer com segurança no ambiente digital.'
          : 'We step in to structure the technological foundation for those who want to grow safely in the digital space.',
    },
    {
      icon: Shield,
      title: language === 'pt' ? 'Foco na engrenagem e não só no clique' : 'Focus on the engine and not just the click',
      description:
        language === 'pt'
          ? 'Agências focam em tráfego. Nós focamos na eficiência e nas automações que realmente seguram o seu cliente.'
          : 'Agencies focus on traffic. We focus on efficiency and the automations that actually retain your customer.',
    },
    {
      icon: LineChart,
      title: language === 'pt' ? 'Sem promessas vazias' : 'No empty promises',
      description:
        language === 'pt'
          ? 'Trabalhamos com clareza total sobre o escopo do projeto, as prioridades e o resultado financeiro esperado.'
          : 'We work with total clarity regarding the project scope, priorities, and expected financial outcomes.',
    },
  ];

  const journey = [
    {
      title: language === 'pt' ? 'Você está dando os primeiros passos' : 'You are taking your first steps',
      description:
        language === 'pt'
          ? 'Criamos a estrutura da loja e organizamos o básico para você vender com segurança e muito profissionalismo.'
          : 'We create the store structure and organize the basics so you can sell safely and very professionally.',
    },
    {
      title: language === 'pt' ? 'Você já vende, mas o caos aumentou' : 'You already sell, but the chaos increased',
      description:
        language === 'pt'
          ? 'Entramos com recuperação de receita, IA no atendimento e integrações precisas para devolver o seu tempo.'
          : 'We step in with revenue recovery, AI in support, and precise integrations to give you your time back.',
    },
    {
      title: language === 'pt' ? 'Você precisa de dados para escalar' : 'You need data to scale',
      description:
        language === 'pt'
          ? 'Consolidamos suas métricas em dashboards visuais e mostramos exatamente onde acelerar a sua operação.'
          : 'We consolidate your metrics into visual dashboards and show exactly where to accelerate your operation.',
    },
  ];

  return (
    <div className="relative overflow-hidden">
      <section className="relative py-20 lg:py-28 bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.14),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(6,182,212,0.10),transparent_30%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(15,23,42,0.35),rgba(2,6,23,0.96))]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4" />
                {language === 'pt' ? 'Automação que gera receita.' : 'Automation that drives revenue.'}
              </div>

              <h1 className="mt-6 font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-100 leading-tight tracking-tight max-w-4xl">
                {language === 'pt'
                  ? 'Transforme sua operação de e-commerce em uma máquina de vendas e escala.'
                  : 'Turn your e-commerce operation into a sales and scaling machine.'}
              </h1>

              <p className="mt-6 text-lg lg:text-xl text-slate-400 leading-relaxed max-w-3xl">
                {language === 'pt'
                  ? 'A ProcessSurge estrutura seu negócio online com loja própria, automações, IA e dados para você parar de apagar incêndios e começar a lucrar.'
                  : 'ProcessSurge structures your online business with custom stores, automations, AI, and data so you can stop putting out fires and start profiting.'}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to={`/${language}/contact`}>
                  <Button className="w-full sm:w-auto bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-bold rounded-full px-7 h-12">
                    {language === 'pt' ? 'Falar com a ProcessSurge' : 'Talk to ProcessSurge'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to={`/${language}/how-it-works`}>
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto rounded-full px-7 h-12 border-slate-700 bg-slate-900/50 text-slate-100 hover:bg-slate-800"
                  >
                    {language === 'pt' ? 'Entender como funciona' : 'See how it works'}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/65 backdrop-blur-sm p-6 lg:p-8 shadow-2xl shadow-cyan-950/20">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
                {language === 'pt' ? 'Onde a ProcessSurge entra' : 'Where ProcessSurge steps in'}
              </p>
              <div className="mt-6 space-y-4">
                {pains.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="flex gap-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <div className="w-11 h-11 rounded-xl bg-cyan-500/12 border border-cyan-500/20 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-cyan-300" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-slate-100">{item.title}</h2>
                        <p className="mt-1 text-sm text-slate-400 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              {language === 'pt' ? 'Os 5 pilares da operação' : 'The 5 pillars of the operation'}
            </p>
            <h2 className="mt-4 font-outfit text-3xl sm:text-4xl font-bold text-slate-100">
              {language === 'pt'
                ? 'Uma estrutura pensada para quem quer vender online com consistência e lucro.'
                : 'A structure designed for businesses that want to sell online with consistency and profit.'}
            </h2>
            <p className="mt-4 text-lg text-slate-400 leading-relaxed">
              {language === 'pt'
                ? 'Em vez de empurrar uma solução isolada, organizamos o que faz a verdadeira diferença no começo e no crescimento acelerado da sua operação.'
                : 'Instead of pushing an isolated solution, we organize what makes a real difference at the start and during the accelerated growth of your operation.'}
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-5 gap-5">
            {pillars.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <div key={pillar.number} className="rounded-3xl border border-slate-800 bg-slate-900/50 p-6 h-full">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold tracking-[0.22em] text-slate-500">{pillar.number}</span>
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/12 border border-cyan-500/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-cyan-300" />
                    </div>
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-100 leading-snug">{pillar.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-900/40 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
                {language === 'pt' ? 'Nosso posicionamento' : 'Our positioning'}
              </p>
              <h2 className="mt-4 font-outfit text-3xl sm:text-4xl font-bold text-slate-100">
                {language === 'pt'
                  ? 'Não somos uma agência. Nem queremos disputar espaço como uma plataforma gigante.'
                  : 'We are not a marketing agency. And we are not trying to be a massive platform.'}
              </h2>
              <p className="mt-4 text-lg text-slate-400 leading-relaxed">
                {language === 'pt'
                  ? 'A ProcessSurge existe para apoiar o empreendedor no começo do e-commerce e continuar ajudando a acelerar quando a estrutura começa a crescer.'
                  : 'ProcessSurge exists to support entrepreneurs at the beginning of their e-commerce journey and keep helping to accelerate as the structure grows.'}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {reasons.map((reason) => {
                const Icon = reason.icon;
                return (
                  <div key={reason.title} className="rounded-3xl border border-slate-800 bg-slate-950/60 p-6">
                    <div className="w-11 h-11 rounded-xl bg-cyan-500/12 border border-cyan-500/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-cyan-300" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-slate-100">{reason.title}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{reason.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              {language === 'pt' ? 'Como evoluímos com você' : 'How we evolve with you'}
            </p>
            <h2 className="mt-4 font-outfit text-3xl sm:text-4xl font-bold text-slate-100">
              {language === 'pt'
                ? 'Entramos no seu momento atual e ajudamos a construir o próximo passo rumo ao lucro.'
                : 'We meet you where you are and help build the next step towards profit.'}
            </h2>
          </div>

          <div className="mt-10 grid lg:grid-cols-3 gap-5">
            {journey.map((item, index) => (
              <div key={item.title} className="rounded-3xl border border-slate-800 bg-slate-900/50 p-6">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-cyan-500 text-slate-950 text-sm font-bold">
                  {index + 1}
                </span>
                <h3 className="mt-5 text-xl font-semibold text-slate-100">{item.title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-900/40 border-t border-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-100">
            {language === 'pt'
              ? 'Quer entender o que faz sentido para o seu momento?'
              : 'Want to understand what makes sense for your current stage?'}
          </h2>
          <p className="mt-4 text-lg text-slate-400 leading-relaxed max-w-3xl mx-auto">
            {language === 'pt'
              ? 'Nós avaliamos a sua operação, entendemos o estágio do negócio e mostramos por onde começar com clareza e foco em resultados.'
              : 'We assess your operation, understand your business stage, and show you where to start with clarity and a focus on results.'}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link to={`/${language}/contact`}>
              <Button className="w-full sm:w-auto bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-bold rounded-full px-7 h-12">
                {language === 'pt' ? 'Solicitar diagnóstico' : 'Request a diagnosis'}
              </Button>
            </Link>
            <Link to={`/${language}/services`}>
              <Button
                variant="outline"
                className="w-full sm:w-auto rounded-full px-7 h-12 border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
              >
                {language === 'pt' ? 'Ver serviços' : 'See services'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
